import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import photoService from '../../services/photo';
import { 
  FaHeart, FaComment, FaEye, FaLock, 
  FaUpload, FaTimes, FaSpinner, FaLockOpen, 
  FaTrash, FaSyncAlt, FaFilter, FaDownload,
  FaShare, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import uploadService from '../../services/upload';
import { useBoxview } from '../../hooks/useBoxview';
import Boxview from '../common/Boxview';
import toast from 'react-hot-toast';
import Loader from '../common/Loader';
import Modal from '../common/Modal';
import './media.css';

const Photos = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalPhotos, setTotalPhotos] = useState(0);
  
  // Filtres
  const [filter, setFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Upload data
  const [uploadData, setUploadData] = useState({
    file: null,
    isPrivate: false
  });

  const navigate = useNavigate();
  const { user } = useAuth();
  const boxview = useBoxview();
  const observerRef = useRef();
  const fileInputRef = useRef();
  
  const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL || 'https://murima.julesferry-ga.com/uploads';

  // Charger les photos
  const loadPhotos = useCallback(async (reset = false) => {
    if (!user?.id) return;
    
    if (reset) {
      setPage(1);
      setPhotos([]);
      setHasMore(true);
    }

    const loadingState = reset ? setLoading : setLoadingMore;
    loadingState(true);

    try {
      const response = await api.get('/get-photos.php', {
        params: { 
          user_id: user.id,
          page: reset ? 1 : page,
          limit: 12,
          filter: filter,
          _t: Date.now()
        }
      });
      
      if (response.data.success) {
        const newPhotos = response.data.photos || [];
        const formattedPhotos = newPhotos.map(photo => ({
          id: photo.id,
          user_id: photo.user_id,
          pseudo: photo.pseudo,
          avatar: photo.avatar,
          url: photo.url,
          est_principale: photo.est_principale,
          est_privee: photo.est_privee,
          date_upload: photo.date_upload,
          likes: photo.likes || 0,
          vues: photo.vues || 0,
          user_liked: Boolean(photo.user_liked),
          can_delete: photo.user_id === user.id
        }));

        setPhotos(prev => reset ? formattedPhotos : [...prev, ...formattedPhotos]);
        setHasMore(response.data.hasMore || false);
        setTotalPhotos(response.data.total || 0);
        
        if (!reset) {
          setPage(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('❌ Error loading photos:', error);
      toast.error('Erreur lors du chargement des photos');
    } finally {
      loadingState(false);
    }
  }, [user?.id, page, filter]);

  // Rafraîchir
  const refreshPhotos = () => {
    loadPhotos(true);
    toast.success('Photos actualisées');
  };

  useEffect(() => {
    if (user?.id) {
      loadPhotos(true);
    }
  }, [user?.id, filter]);

  // Infinite scroll
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '20px',
      threshold: 0.1
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasMore && !loadingMore && !loading) {
        loadPhotos();
      }
    }, options);

    const sentinel = document.getElementById('sentinel');
    if (sentinel) observerRef.current.observe(sentinel);

    return () => observerRef.current?.disconnect();
  }, [hasMore, loadingMore, loading, loadPhotos]);

  // Upload
  const handleUploadChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG, GIF ou WEBP');
      e.target.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 10MB');
      e.target.value = '';
      return;
    }

    setUploadData(prev => ({ ...prev, file }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadData.file) {
      toast.error('Veuillez sélectionner une photo');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const result = await uploadService.uploadPhoto(
        uploadData.file,
        'photo',
        uploadData.isPrivate,
        (progress) => setUploadProgress(progress)
      );

      if (result.success) {
        toast.success('Photo publiée avec succès !');
        setShowUploadModal(false);
        resetUploadForm();
        refreshPhotos();
      } else {
        toast.error(result.error || 'Erreur lors de l\'upload');
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Like
  const handleLike = async (photoId, e) => {
    e?.stopPropagation();
    
    if (!user?.id) {
      toast.error('Connectez-vous pour liker');
      return;
    }

    const photo = photos.find(p => p.id === photoId);
    if (!photo) return;

    const wasLiked = photo.user_liked;
    const previousLikes = photo.likes;
    
    // Optimistic update
    setPhotos(prev =>
      prev.map(p =>
        p.id === photoId
          ? {
              ...p,
              likes: wasLiked ? p.likes - 1 : p.likes + 1,
              user_liked: !wasLiked
            }
          : p
      )
    );

    try {
      const result = await photoService.likePhoto(photoId, user.id);

      if (!result.success) {
        // Rollback en cas d'erreur
        setPhotos(prev =>
          prev.map(p =>
            p.id === photoId
              ? { ...p, likes: previousLikes, user_liked: wasLiked }
              : p
          )
        );
        toast.error(result.error || 'Erreur lors du like');
      }
    } catch (error) {
      // Rollback
      setPhotos(prev =>
        prev.map(p =>
          p.id === photoId
            ? { ...p, likes: previousLikes, user_liked: wasLiked }
            : p
        )
      );
      toast.error('Erreur réseau');
    }
  };

  // Vues
  const openPhotoModal = (photo) => {
    setSelectedPhoto(photo);
    setShowPhotoModal(true);
    
    // Optimistic update pour les vues
    setPhotos(prev =>
      prev.map(p =>
        p.id === photo.id ? { ...p, vues: p.vues + 1 } : p
      )
    );

    // Appel API en arrière-plan
    photoService.incrementViews(photo.id).catch(console.error);
  };

  // Ouvrir avec Boxview
// Ouvrir avec Boxview
const openPhotoBoxview = (photo, index) => {
  const images = photos.map(p => ({
    url: getPhotoUrl(p),
    caption: `Photo de ${p.pseudo}`,
    user: p
  }));
  
  boxview.openBoxview(images, index);
  
  // Incrémenter les vues pour la photo courante
  setPhotos(prev =>
    prev.map(p =>
      p.id === photo.id ? { ...p, vues: p.vues + 1 } : p
    )
  );

  // Appel API en arrière-plan
  photoService.incrementViews(photo.id).catch(console.error);
};

  // Suppression
  const confirmDeletePhoto = (photoId, e) => {
    e.stopPropagation();
    setPhotoToDelete(photoId);
    setShowDeleteConfirm(true);
  };

  const handleDeletePhoto = async () => {
    if (!photoToDelete) return;

    try {
      const response = await api.post('/delete-photo.php', {
        photo_id: photoToDelete,
        user_id: user.id
      });

      if (response.data.success) {
        setPhotos(prev => prev.filter(p => p.id !== photoToDelete));
        toast.success('Photo supprimée');
        
        if (selectedPhoto?.id === photoToDelete) {
          setShowPhotoModal(false);
          setSelectedPhoto(null);
        }
      } else {
        toast.error(response.data.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('❌ Delete error:', error);
      toast.error('Erreur réseau');
    } finally {
      setShowDeleteConfirm(false);
      setPhotoToDelete(null);
    }
  };

  // Formateurs
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num.toString();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return 'Hier';
    if (diff < 7) return `Il y a ${diff} jours`;
    return date.toLocaleDateString('fr-FR');
  };

  const getPhotoUrl = (photo) => {
    if (!photo?.url) return '';
    if (photo.url.startsWith('http')) return photo.url;
    return `${UPLOADS_URL}/${photo.url}`;
  };

  const getAvatarUrl = (avatar) => {
    if (!avatar) return 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e';
    if (avatar.startsWith('http')) return avatar;
    return `${UPLOADS_URL}/${avatar}`;
  };

  const resetUploadForm = () => {
    setUploadData({ file: null, isPrivate: false });
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (loading && photos.length === 0) {
    return <Loader />;
  }

  return (
    <div className="media-page">
      {/* Header */}
      <div className="media-header">
        <div className="header-left">
          <h1>Photos </h1>
          <button 
            className="btn-filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
          </button>
        </div>
        
        <div className="header-actions">
          <button 
            className="btn-refresh" 
            onClick={refreshPhotos}
            disabled={loading}
          >
            <FaSyncAlt className={loading ? 'spin' : ''} />
          </button>
          <button 
            className="btn-upload" 
            onClick={() => setShowUploadModal(true)}
          >
            <FaUpload /> Publier
          </button>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="filters-bar">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Toutes ({totalPhotos})
          </button>
          <button 
            className={`filter-btn ${filter === 'mine' ? 'active' : ''}`}
            onClick={() => setFilter('mine')}
          >
            Mes photos
          </button>
          <button 
            className={`filter-btn ${filter === 'matches' ? 'active' : ''}`}
            onClick={() => setFilter('matches')}
          >
            Matches
          </button>
        </div>
      )}

      {/* Grille */}
      {photos.length === 0 ? (
        <div className="no-media">
          <FaUpload className="no-media-icon" />
          <h3>Aucune photo pour le moment</h3>
          <p>Soyez le premier à partager une photo !</p>
          <button className="btn-upload-large" onClick={() => setShowUploadModal(true)}>
            <FaUpload /> Publier une photo
          </button>
        </div>
      ) : (
        <>
          <div className="photos-grid">
            {photos.map((photo, index) => (
              <div key={photo.id} className="photo-card">
                <div 
                  className="photo-image"
                  onClick={() => openPhotoBoxview(photo, index)}
                >
                  <img 
                    src={getPhotoUrl(photo)} 
                    alt={`Photo de ${photo.pseudo}`}
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e';
                    }}
                  />
                  
                  {photo.est_privee && (
                    <div className="private-badge">
                      <FaLock /> Privé
                    </div>
                  )}

                  {photo.can_delete && (
                    <button 
                      className="btn-delete-photo"
                      onClick={(e) => confirmDeletePhoto(photo.id, e)}
                      title="Supprimer la photo"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>

                <div className="photo-overlay">
                  <div 
                    className="photo-user"
                    onClick={() => navigate(`/profile/${photo.user_id}`)}
                  >
                    <img src={getAvatarUrl(photo.avatar)} alt={photo.pseudo} />
                    <span>{photo.pseudo}</span>
                  </div>

                  <div className="photo-stats">
                    <span title="Vues"><FaEye /> {formatNumber(photo.vues)}</span>
                    <span title="Likes"><FaHeart className={photo.user_liked ? 'liked' : ''} /> {formatNumber(photo.likes)}</span>
                  </div>

                  <div className="photo-actions">
                    <button 
                      className={`btn-like-small ${photo.user_liked ? 'liked' : ''}`}
                      onClick={(e) => handleLike(photo.id, e)}
                      title={photo.user_liked ? 'Je n\'aime plus' : 'J\'aime'}
                    >
                      <FaHeart />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Infinite scroll sentinel */}
          <div id="sentinel" className="sentinel">
            {loadingMore && (
              <div className="loading-more">
                <FaSpinner className="spinner" /> Chargement...
              </div>
            )}
            {!hasMore && photos.length > 0 && (
              <div className="no-more">
                <p>Plus aucune photo à charger</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal upload */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => {
          if (!uploading) {
            setShowUploadModal(false);
            resetUploadForm();
          }
        }}
        title="Publier une photo"
        size="medium"
      >
        <form onSubmit={handleUpload} className="upload-form">
          <div className="form-group">
            <label>
              Photo <span className="required">*</span>
            </label>
            <div className="file-upload-area">
              <input
                type="file"
                ref={fileInputRef}
                id="photo-upload"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleUploadChange}
                disabled={uploading}
              />
              
              <label htmlFor="photo-upload" className="file-upload-label">
                {uploadData.file ? (
                  <div className="file-selected">
                    <div className="file-info">
                      <span className="file-name" title={uploadData.file.name}>
                        {uploadData.file.name.length > 30 
                          ? `${uploadData.file.name.substring(0, 27)}...` 
                          : uploadData.file.name}
                      </span>
                      <span className="file-size">
                        {(uploadData.file.size / (1024 * 1024)).toFixed(2)} MB
                      </span>
                    </div>
                    <button 
                      type="button" 
                      className="btn-remove-file"
                      onClick={(e) => {
                        e.preventDefault();
                        setUploadData(prev => ({ ...prev, file: null }));
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      disabled={uploading}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <div className="file-upload-placeholder">
                    <FaUpload />
                    <span>Cliquez pour sélectionner une photo</span>
                    <small>JPG, PNG, GIF, WEBP - Max 10MB</small>
                  </div>
                )}
              </label>
            </div>
          </div>

          {uploadProgress > 0 && (
            <div className="upload-progress">
              <div className="progress-label">
                <span>Upload en cours...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}

          <div className="form-group checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isPrivate"
                checked={uploadData.isPrivate}
                onChange={(e) => setUploadData(prev => ({ ...prev, isPrivate: e.target.checked }))}
                disabled={uploading}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
  <span className="checkbox-custom">
    {uploadData.isPrivate ? <FaLock /> : <FaLockOpen />}
  </span>
  <span className="checkbox-text">
    Privée (visible uniquement par vos matches)
  </span>
</div>
            </label>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-cancel" 
              onClick={() => {
                setShowUploadModal(false);
                resetUploadForm();
              }}
              disabled={uploading}
            >
              Annuler
            </button>
            <button 
              type="submit" 
              className="btn-submit"
              disabled={uploading || !uploadData.file}
            >
              {uploading ? (
                <>
                  <FaSpinner className="spinner" /> Publication... {uploadProgress}%
                </>
              ) : (
                'Publier la photo'
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal suppression */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setPhotoToDelete(null);
        }}
        title="Confirmer la suppression"
        size="small"
      >
        <div className="delete-confirm-content">
          <p>Êtes-vous sûr de vouloir supprimer cette photo ?</p>
          <p className="delete-warning">Cette action est irréversible.</p>
          <div className="delete-confirm-actions">
            <button 
              className="btn-cancel"
              onClick={() => {
                setShowDeleteConfirm(false);
                setPhotoToDelete(null);
              }}
            >
              Annuler
            </button>
            <button 
              className="btn-delete-confirm"
              onClick={handleDeletePhoto}
            >
              <FaTrash /> Supprimer
            </button>
          </div>
        </div>
      </Modal>

      {/* Boxview */}
      <Boxview 
        isOpen={boxview.isOpen}
        onClose={boxview.closeBoxview}
        images={boxview.images}
        initialIndex={boxview.currentIndex}
      />
    </div>
  );
};

export default Photos;